import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setAuthError } from "../../redux/slice/authSlice";
import { initiateAuth } from "../../redux/auth";
import TextInput from "../../components/formInputs/TextInput";
import Button from "../../components/buttons/Button";
import MangoLogo from "../../components/icons/MangoLogo";
import EmailIcon from "../../components/icons/EmailIcon";
import "../../components/styles/loginPage.scss";

/**
 * LoginPage — MangoTV "Get Started" screen
 * Unified email-only auth: enter email → OTP → auto sign-up or sign-in
 */

const LoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoading, error } = useSelector((state) => state.auth);

  const [email, setEmail] = useState("");

  const isFormValid = email.trim() !== "";

  const handleGetStarted = useCallback(async () => {
    if (!isFormValid || isLoading) return;
    dispatch(setAuthError(null));

    await initiateAuth(email.trim(), navigate);
  }, [isFormValid, isLoading, dispatch, email, navigate]);

  return (
    <div className="login-page">
      {/* ===== LEFT: Onboarding Sidebar ===== */}
      <div className="login-page__image-container">
        <img
          src="/assets/onboarding-sidebar-bg.jpg"
          alt=""
          className="login-page__sidebar-bg"
        />
      </div>

      {/* ===== RIGHT: Form Container ===== */}
      <div className="login-page__container">
        {/* Background dots pattern */}
        <div className="login-page__bg-pattern">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <circle cx="215" cy="493" r="2" fill="#404040" opacity="0.5" />
            <circle cx="241" cy="333" r="2" fill="#404040" opacity="0.5" />
            <circle cx="328" cy="129" r="2" fill="#404040" opacity="0.5" />
            <circle cx="45" cy="835" r="2" fill="#404040" opacity="0.5" />
            <circle cx="492" cy="975" r="2" fill="#404040" opacity="0.5" />
            <circle cx="794" cy="943" r="2" fill="#404040" opacity="0.5" />
            <circle cx="861" cy="694" r="2" fill="#404040" opacity="0.5" />
            <circle cx="772" cy="207" r="2" fill="#404040" opacity="0.5" />
            <circle cx="593" cy="66" r="2" fill="#404040" opacity="0.5" />
          </svg>
        </div>

        {/* Form Wrapper */}
        <div className="login-page__wrapper">
          <div className="login-page__content">
            {/* Title Section */}
            <div className="login-page__title-section">
              {/* MangoTV Logo */}
              <MangoLogo size={80} />

              <h1 className="login-page__title">Get Started</h1>
              <p className="login-page__subtitle">
                Stream movies, sports, live shows, and more&mdash;anytime, anywhere
              </p>
            </div>

            {/* Form Group */}
            <div className="login-page__form-group">
              <div className="login-page__form">
                <TextInput
                  label="Email Address"
                  value={email}
                  onChange={setEmail}
                  placeholder="eg. yami@yahoo.com"
                  type="email"
                  leftIcon={<EmailIcon />}
                />
              </div>

              {/* Error */}
              {error && (
                <p style={{ color: "#ef4444", fontSize: "14px", textAlign: "center" }}>
                  {error}
                </p>
              )}

              {/* Get Started Button */}
              <Button
                label="Get Started"
                size="lg"
                variant="primary"
                isDisabled={!isFormValid}
                isLoading={isLoading}
                action={handleGetStarted}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
